import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown as Drop, Form, Col } from 'react-bootstrap';

import { ScrollBox } from 'components/common/containers';
import SearchInput from 'components/common/inputs/SearchInput';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import Button from 'components/common/buttons/Button';
import Checkbox from 'components/common/controls/Checkbox';

import * as Styled from './NewDropdown.styles';

const MAX_ITEMS_SHOWN = 500;

interface DropdownProps {
  isMultiSelect?: boolean;
  items?: Record<string, number | string>;
  selectedValues?: (number | string)[];
  isShowingSelectedValues?: boolean;
  onSelectedValuesChange: (newValues: (number | string)[]) => void;
  placeholder?: string;
  isSearchable?: boolean;
  searchShowsSelectedValue?: boolean;
  onSearch?: (value: string) => void;
  valueIsImage?: boolean;
  isLoading?: boolean;
  hasSelectAllBtn?: boolean;
  hasClearAllBtn?: boolean;
  confirmSelectionsBtn?: boolean;
  canUnselectItem?: boolean;
  closeOnSelect?: boolean;
  simpleToggle?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  skipFiltering?: boolean;
  customLabels?: (React.ReactNode | string)[];
}

const Dropdown: FunctionComponent<React.PropsWithChildren<DropdownProps>> = ({
  isMultiSelect = false,
  items = {},
  selectedValues = [],
  isShowingSelectedValues = true,
  onSelectedValuesChange,
  placeholder = 'Select',
  isSearchable = false,
  searchShowsSelectedValue = true,
  onSearch = null,
  valueIsImage = false,
  isLoading = false,
  hasSelectAllBtn = false,
  hasClearAllBtn = false,
  confirmSelectionsBtn = false,
  canUnselectItem = true,
  closeOnSelect = false,
  simpleToggle = false,
  hasError = false,
  disabled = false,
  skipFiltering = false,
  customLabels = []
}) => {
  const dropdownRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchString, setSearchString] = useState('');

  // handles exiting the dropdown by clicking outside of it
  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearchInputChange = (newSearchString: string): void => {
    setSearchString(newSearchString);
    if (onSearch) {
      onSearch(newSearchString);
    }
  };

  const selectValue = (value: number | string): void => {
    if (isMultiSelect) {
      onSelectedValuesChange([...selectedValues, value]);
    } else {
      onSelectedValuesChange([value]);
    }
  };

  const unselectValue = (value: number | string): void => {
    if (isMultiSelect) {
      const updatedValues = [...selectedValues];
      updatedValues.splice(updatedValues.indexOf(value), 1);
      onSelectedValuesChange(updatedValues);
    } else if (canUnselectItem) {
      onSelectedValuesChange([]);
    }
  };

  const clickOnItem = (value: number | string, isSelected: boolean): void => {
    if (isSelected) {
      unselectValue(value);
    } else {
      selectValue(value);
    }
    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  const onSelectAll = (allItems: [string, number | string][]): void => {
    onSelectedValuesChange(allItems.map((item): number | string => item[1]));
    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  const onClearAll = (): void => {
    onSelectedValuesChange([]);
    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  const labels = customLabels.length > 0 ? customLabels : Object.keys(items);
  const values = Object.values(items);

  const filteredItems = skipFiltering
    ? Object.entries(items)
    : Object.entries(items)
        .filter(
          ([label]) => !searchString || label.toLowerCase().includes(searchString.toLowerCase())
        )
        .slice(0, MAX_ITEMS_SHOWN);

  return (
    <Drop ref={dropdownRef} show={isOpen} drop="down">
      <Styled.Toggle
        data-testid="dropdown"
        variant="white"
        onClick={(): void => setIsOpen(!isOpen)}
        $hasError={hasError === true}
        $simpleToggle={simpleToggle}
        disabled={disabled}
      >
        {!isMultiSelect &&
        selectedValues.length > 0 &&
        selectedValues[0] !== undefined &&
        selectedValues[0] !== null
          ? labels[values.indexOf(selectedValues[0])]
          : placeholder}
      </Styled.Toggle>
      {isMultiSelect && (
        <Styled.TagsWrapper>
          {isShowingSelectedValues &&
            selectedValues?.map((value, index) => (
              <Styled.Tag key={index}>
                {labels[values.indexOf(value)]}
                {!disabled && (
                  <Styled.TagCloseBtn type="button" onClick={(): void => unselectValue(value)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </Styled.TagCloseBtn>
                )}
              </Styled.Tag>
            ))}
        </Styled.TagsWrapper>
      )}
      {isOpen && (
        <Styled.DropdownContent>
          {isSearchable && (
            <Drop.Header>
              <SearchInput value={searchString} setValue={handleSearchInputChange} />
            </Drop.Header>
          )}
          {isMultiSelect && (hasSelectAllBtn || hasClearAllBtn) && (
            <Styled.ButtonSection>
              {hasSelectAllBtn && (
                <Button secondary compact onClick={(): void => onSelectAll(filteredItems)}>
                  Select all
                </Button>
              )}
              {hasClearAllBtn && (
                <Button secondary compact onClick={onClearAll}>
                  Clear
                </Button>
              )}
            </Styled.ButtonSection>
          )}
          {isSearchable && !isLoading && filteredItems.length === 0 && searchString !== '' && (
            <Styled.NoResultsText>{`No results for "${searchString}"`}</Styled.NoResultsText>
          )}
          <ScrollBox>
            {isLoading ? (
              <div className="tw-mt-20">
                <LoadingSpinner />
              </div>
            ) : (
              filteredItems.map(([label, value]) => {
                const isSelected = selectedValues.includes(value);
                if (isSelected && !searchShowsSelectedValue) {
                  return null;
                }
                return (
                  <Styled.DropdownItem
                    key={label}
                    onClick={(): void => clickOnItem(value, isSelected)}
                    $compact={valueIsImage}
                    $isMultiSelect={isMultiSelect}
                  >
                    <Form.Row>
                      {isMultiSelect && (
                        <Col xs="auto">
                          <Checkbox
                            checked={isSelected}
                            text={
                              <Styled.ItemCol>
                                {valueIsImage ? (
                                  <Styled.Image src={`${value}`} alt={label} />
                                ) : (
                                  labels[values.indexOf(value)]
                                )}
                              </Styled.ItemCol>
                            }
                          />
                        </Col>
                      )}
                      {!isMultiSelect && (
                        <Styled.ItemCol>
                          {valueIsImage ? (
                            <Styled.Image src={`${value}`} alt={label} />
                          ) : (
                            labels[values.indexOf(value)]
                          )}
                        </Styled.ItemCol>
                      )}
                    </Form.Row>
                  </Styled.DropdownItem>
                );
              })
            )}
            {confirmSelectionsBtn && (
              <Styled.SelectButtonWrapper>
                <Button onClick={(): void => setIsOpen(false)}>Select</Button>
              </Styled.SelectButtonWrapper>
            )}
          </ScrollBox>
        </Styled.DropdownContent>
      )}
    </Drop>
  );
};

export default Dropdown;
